import React, { useEffect } from "react";
import { Box, Stack, styled, Typography } from "@mui/material";

import ConsoleAccordion, { ConsoleAccordionProps } from "./ConsoleAccordion/media-player-console-accordion";
import { useTranslation } from "react-i18next";

export type MediaPlayerConsoleProps = {
  mediaList: Omit<ConsoleAccordionProps, "onClickPlay" | "onClickAccordion" | "isExpanded">[];
  allLang: string[];
};

const MediaPlayerConsole: React.FC<MediaPlayerConsoleProps> = (props) => {
  const { t } = useTranslation();
  const [activeAccordion, setActiveAccordion] = React.useState<number>(0);
  const [list, setList] = React.useState<any[]>([]);
  const $consoleBody = React.useRef<HTMLDivElement>(undefined);

  const scrollToTop = React.useCallback((index: number) => {
    const pos = 48.5 * index;
    $consoleBody.current?.scrollTo(0, pos);
  }, []);

  const onClickAccordion = React.useCallback(
    (index: number) => {
      setActiveAccordion((prevState) => (prevState === index ? -1 : index));
      scrollToTop(index);
    },
    [scrollToTop]
  );

  useEffect(() => {
    let sorted = props.mediaList
      .map((x) => x)
      .sort(function (a, b) {
        return a.title.localeCompare(b.title);
      });
    setList(sorted);
  }, [props.mediaList]);

  return (
    <Console>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack>
          <Typography color="text.primary" variant="subtitle2" component="h3" sx={{ mb: 0.5 }}>
            {props.mediaList.length} {t("common:lowerCaseContents")}
          </Typography>
          <Typography color="text.tier" variant="body1" component="p">
            {props.allLang.length} {t("common:versions")}
          </Typography>
        </Stack>
      </Stack>
      <ConsoleBody ref={$consoleBody}>
        {list.map((media, index) => (
          <Box sx={{ mb: 1 }} key={media.title}>
            <ConsoleAccordion
              {...media}
              onClickAccordion={() => onClickAccordion(index)}
              isExpanded={activeAccordion === index}
            />
          </Box>
        ))}
      </ConsoleBody>
    </Console>
  );
};

export default MediaPlayerConsole;

const MediaPlayer = styled(Box)(({ theme }) => ({
  flex: "0 1 55%",
  alignSelf: "flex-start",
  borderRadius: "6px 0 0 6px",
  overflow: "hidden",
  borderRight: `1px solid ${theme.palette.divider}`,
}));

const StyledStack = styled(Stack)(({ theme }) => ({
  borderRadius: 6,
  position: "relative",
}));

const Console = styled(Stack)(({ theme }) => ({
  position: "absolute",
  right: 0,
  top: 0,
  bottom: 0,
  width: "45%",
  paddingLeft: theme.spacing(2),
  padding: theme.spacing(2),
  paddingBottom: 0,
  background: theme.palette.background.paper,
  borderRadius: "0 6px 6px 0",
}));

const ConsoleBody = styled(Box)(({ theme }) => ({
  overflow: "auto",
  flex: 1,
  ...theme.helpers(theme).scrollBar,
}));

export const MediaPlayerConsoleStyled = {
  StyledStack,
  MediaPlayer,
  Console,
  ConsoleBody,
};
